import React, { FC, useRef } from "react";
import styled from "styled-components";
import { rem, vwDesktop, vwMobile } from "../../helpers/styles";
import { MEDIA_DESKTOP } from "../../helpers/theme";
import SectionLines from "../SectionLines";
import HorizontalSectionLines from "../HorizontalSectionLines";

export type FiftyFiftyImageProps = {
  headline: string;
  description: string;
  ctaLabel: string;
  ctaUrl: string;

  imageUrl: string;
  imageAlt: string;

  className?: string;
};

export const FiftyFiftyImage: FC<FiftyFiftyImageProps> = ({
  headline,
  description,
  ctaLabel,
  ctaUrl,
  imageUrl,
  imageAlt,
}: FiftyFiftyImageProps) => {
  const titleIdRef = useRef(
    `fifty-fifty-image-${Math.random().toString(36).slice(2, 9)}`,
  );
  const titleId = titleIdRef.current;

  return (
    <Section aria-labelledby={titleId}>
      <Grid>
        <ContentCol>
          <Headline id={titleId}>{headline}</Headline>

          <Description dangerouslySetInnerHTML={{ __html: description }} />

          <CtaWrap>
            <PrimaryCta href={ctaUrl} target="_blank" rel="noopener noreferrer">
              <span>{ctaLabel}</span>
              <ExternalIcon viewBox="0 0 24 24" aria-hidden="true">
                <g clipPath="url(#clip0_3_683)">
                  <path
                    d="M6.36999 18.75C6.06999 18.75 5.80999 18.64 5.57999 18.41C5.35999 18.18 5.23999 17.92 5.23999 17.62V6.36999C5.23999 6.06999 5.34999 5.80999 5.57999 5.57999C5.79999 5.34999 6.06999 5.23999 6.36999 5.23999H9.03999C9.19999 5.23999 9.32999 5.28999 9.43999 5.39999C9.54999 5.50999 9.59999 5.63999 9.59999 5.79999C9.59999 5.95999 9.54999 6.08999 9.43999 6.19999C9.32999 6.30999 9.19999 6.35999 9.03999 6.35999H6.36999V17.61H17.62V14.94C17.62 14.78 17.67 14.65 17.78 14.54C17.89 14.43 18.02 14.38 18.18 14.38C18.34 14.38 18.47 14.43 18.58 14.54C18.69 14.65 18.74 14.78 18.74 14.94V17.61C18.74 17.91 18.63 18.17 18.4 18.4C18.17 18.63 17.91 18.74 17.61 18.74H6.36999V18.75ZM17.63 7.17999L10.56 14.25C10.45 14.36 10.32 14.42 10.17 14.41C10.02 14.4 9.88999 14.34 9.77999 14.23C9.66999 14.12 9.60999 13.99 9.60999 13.84C9.60999 13.69 9.66999 13.56 9.77999 13.45L16.84 6.36999H13.3C13.14 6.36999 13.01 6.31999 12.9 6.20999C12.79 6.09999 12.74 5.96999 12.74 5.80999C12.74 5.64999 12.79 5.51999 12.9 5.40999C13.01 5.29999 13.14 5.24999 13.3 5.24999H18.19C18.35 5.24999 18.48 5.29999 18.59 5.40999C18.7 5.51999 18.75 5.64999 18.75 5.80999V10.7C18.75 10.86 18.7 10.99 18.59 11.1C18.48 11.21 18.35 11.26 18.19 11.26C18.03 11.26 17.9 11.21 17.79 11.1C17.68 10.99 17.63 10.86 17.63 10.7V7.17999Z"
                    fill="currentColor"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_3_683">
                    <rect
                      width="13.5"
                      height="13.5"
                      fill="white"
                      transform="translate(5.25 5.25)"
                    />
                  </clipPath>
                </defs>
              </ExternalIcon>
            </PrimaryCta>
          </CtaWrap>
        </ContentCol>

        <ImageCol>
          <ImageFrame aria-hidden="true" />
          <KitchenImage src={imageUrl} alt={imageAlt} />
        </ImageCol>
      </Grid>
    </Section>
  );
};

export default FiftyFiftyImage;

const Section = styled.section`
  position: relative;
  background: #f6f1e7;
  overflow: hidden;

  padding-top: ${vwMobile(80)};
  padding-bottom: ${vwMobile(80)};
  padding-left: ${vwMobile(20)};
  padding-right: ${vwMobile(20)};

  ${MEDIA_DESKTOP} {
    padding-top: ${vwDesktop(120)};
    padding-bottom: ${vwDesktop(120)};
    padding-left: ${vwDesktop(128)};
    padding-right: ${vwDesktop(80)};
  }

  ${SectionLines("#012604")};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${vwMobile(50)};
  align-items: center;

  ${MEDIA_DESKTOP} {
    grid-template-columns: 1fr 1fr;
    gap: ${vwDesktop(48)};
  }
`;

const ContentCol = styled.div`
  order: 2;
  padding-right: ${vwMobile(20)};
  padding-left: ${vwMobile(20)};

  ${MEDIA_DESKTOP} {
    order: 1;
    padding-right: ${vwDesktop(106)};
    padding-left: 0;
  }
`;

const Headline = styled.h2`
  margin: 0;
  color: #012604;
  font-family: "GT Alpina";
  font-weight: 300;
  text-align: center;
  font-size: ${rem(40)};
  line-height: 95%;
  letter-spacing: ${rem(-2)};

  ${MEDIA_DESKTOP} {
    text-align: left;
    font-size: ${rem(72)};
    letter-spacing: ${rem(-3.6)};
    max-width: ${vwDesktop(520)};
    margin-left: 0;
    margin-right: 0;
  }
`;

const Description = styled.div`
  color: #012604;
  font-family: "Sharp Sans Display No1", sans-serif;
  font-weight: 500;
  line-height: 130%;
  font-size: ${rem(12)};
  text-align: center;

  margin-top: ${vwMobile(32)};

  ${MEDIA_DESKTOP} {
    font-size: ${rem(16)};
    text-align: left;
    margin-top: ${vwDesktop(40)};
    margin-right: ${vwDesktop(16)};
  }
`;

const CtaWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${vwMobile(20)};

  ${MEDIA_DESKTOP} {
    justify-content: flex-start;
    margin-top: ${vwDesktop(40)};
  }
`;

const PrimaryCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  border-radius: ${rem(431.177)};
  background: #b5dd71;
  color: #012604;

  font-family: "GT Alpina";
  font-weight: 300;
  line-height: 100%;
  text-decoration: none;

  font-size: ${rem(12)};
  letter-spacing: ${rem(-0.6)};
  padding: ${vwMobile(13)} ${vwMobile(32)};

  ${MEDIA_DESKTOP} {
    font-size: ${rem(16)};
    letter-spacing: ${rem(-0.64)};
    padding: ${vwDesktop(16)} ${vwDesktop(32)};
  }

  &:hover {
    background: #012604;
    color: #b5dd71;
  }

  &:focus-visible {
    outline: 1px solid #012604;
    outline-offset: 3px;
  }
`;

const ImageCol = styled.div`
  order: 1;
  position: relative;

  ${MEDIA_DESKTOP} {
    order: 2;
    display: flex;
    justify-content: center;
  }
  ${HorizontalSectionLines("#012604")};
`;

const ImageFrame = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: ${vwMobile(329)};
  height: ${vwMobile(329)};
  border-radius: 50%;
  border: 1px solid #012604;

  pointer-events: none;

  ${MEDIA_DESKTOP} {
    width: ${vwDesktop(553)};
    height: ${vwDesktop(553)};
    transform: translate(-50%, -50%);
  }
`;

const KitchenImage = styled.img`
  position: relative;
  z-index: -1;
  width: ${vwMobile(249)};
  height: ${vwMobile(343.08)};

  border-radius: ${rem(13.234)};
  display: block;
  object-fit: cover;

  margin: 0 auto;

  ${MEDIA_DESKTOP} {
    width: ${vwDesktop(442)};
    height: ${vwDesktop(609)};
    margin: 0;
  }
`;
const ExternalIcon = styled.svg`
  width: ${rem(14)};
  height: ${rem(14)};
  margin-left: ${rem(8)};
  flex-shrink: 0;
`;
